import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { CustomHttpInterceptor } from './shared/interceptor/httpInterceptor';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CookieService } from 'ngx-cookie-service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { AnnotationService } from '@services/annotation/annotation.service';
import { NgxAiChatModule } from 'ngx-ai-chat';
import { Router } from '@angular/router';
import { AuthInitializationResult, AuthInitializationService } from '@services/auth-initialization.service';

// Factory function
export function appInitializerFactory(
  authInitService: AuthInitializationService,
  router: Router
): () => Promise<void> {
  return async () => {
    console.log('APP_INITIALIZER: Running appInitializerFactory...');
    const result: AuthInitializationResult = await authInitService.initializeAppAuth();
    const currentPath = window.location.pathname; // Or decode from router.url if more reliable early
    console.log('APP_INITIALIZER: Current path:', currentPath);
    // Capture all current query parameters *before* any navigation
    const initialQueryString = window.location.search;
    console.log('APP_INITIALIZER: Initial query string:', initialQueryString);
    const allInitialParams = new URLSearchParams(initialQueryString);
    console.log('APP_INITIALIZER: All initial query parameters:', JSON.stringify(allInitialParams));
    
    const preservedParams: { [key: string]: string } = {};
    allInitialParams.forEach((value, key) => {
      if (key.toLowerCase() !== 'token' && key.toLowerCase() !== 'auth') {
        preservedParams[key] = value;
      }
    });
    console.log('APP_INITIALIZER: Preserved params:', preservedParams);

    if (result.tokenProcessed) {
      console.log('APP_INITIALIZER: Token was processed.', result);
      if (result.loggedIn) {
        // IMPORTANT: Use result.redirectTo which contains the original deep link or default.
        const targetUrl = result.redirectTo || '/dashboard/chats'; 
        console.log(`APP_INITIALIZER: Token valid, navigating to ${targetUrl} with preserved params.`);
        await router.navigate([targetUrl], { queryParams: preservedParams, replaceUrl: true });
      } else {
        console.log('APP_INITIALIZER: Token invalid, navigating to /auth/login with preserved params.');
        await router.navigate(['/auth/login'], { queryParams: preservedParams, replaceUrl: true });
      }
    } else {
      console.log('APP_INITIALIZER: No token processed from URL.', result);
      // No token in URL, but user might be logged in from a previous session.
      if (result.loggedIn && (currentPath === '/auth/login' || currentPath === '/' || currentPath === '')) {
        // If logged in and on a generic entry page, redirect to dashboard.
        // Preserve all original params as no auth token was processed to be removed.
        console.log('APP_INITIALIZER: Logged in, on entry page, redirecting to dashboard, preserving params.');
        await router.navigate(['/dashboard/chats'], { queryParamsHandling: 'preserve', replaceUrl: true });
      } else {
        // Otherwise, let Angular handle the routing to the currentPath normally.
        // If currentPath itself had query params (like 'iframe' but no 'token'/'auth'), they will be naturally preserved by Angular's default routing.
        console.log('APP_INITIALIZER: No token, not on entry page or not logged in. No explicit navigation by initializer.');
      }
    }
  };
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    SharedModule,
    HttpClientModule,
    ToastrModule.forRoot({
      closeButton: false,
      enableHtml: true,
      positionClass: 'toast-top-right'
    }),
    BrowserAnimationsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    NgxAiChatModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true,
    },
    CookieService,
    AnnotationService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [AuthInitializationService, Router],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
